import { useState } from 'react';
import {
  Card, CardContent, Typography, Skeleton, Alert, Stack, Box,
  Button, TextField, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useClients } from '../hooks/useClients';
import { NewClientDialog } from './NewClientDialog';
import { RotateSecretDialog } from './RotateSecretDialog';
import type { CreateClientResult, RotateClientResult } from '../api/clients';

export function ClientsSection() {
  const { clients, isLoading, isError, error, create, rotate, remove } = useClients();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [createResult, setCreateResult] = useState<CreateClientResult | null>(null);

  const [rotateResult, setRotateResult] = useState<RotateClientResult | null>(null);
  const [rotateClientId, setRotateClientId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const onCreate = async () => {
    const name = newName.trim() || `client-${Date.now()}`;
    const result = await create.mutateAsync(name);
    setCreateResult(result);
    setNewName('');
    setCreateOpen(false);
  };

  const onRotate = async (clientId: string) => {
    setRotateClientId(clientId);
    const result = await rotate.mutateAsync(clientId);
    setRotateResult(result);
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    await remove.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  const copyId = async (clientId: string) => {
    try {
      await navigator.clipboard.writeText(clientId);
      setCopiedId(clientId);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* ignore */ }
  };

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h3">OAuth clients</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={create.isPending}
          >
            New client
          </Button>
        </Stack>

        <Typography variant="body2" sx={{ color: 'var(--ink-muted)', mb: 3 }}>
          Create OAuth clients to call the API from your scripts and services.
          Each client uses the client credentials grant — authenticate with
          your <code>client_id</code> + <code>client_secret</code> to get a
          1-hour access token.
        </Typography>

        {isLoading && <Skeleton variant="rectangular" height={120} />}

        {isError && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {(error as Error).message || 'Could not load clients'}
          </Alert>
        )}

        {!isLoading && !isError && clients.length === 0 && (
          <Box sx={{ color: 'var(--ink-muted)', fontSize: '0.875rem', py: 2 }}>
            No OAuth clients yet. Create one to start using the API.
          </Box>
        )}

        {clients.length > 0 && (
          <Stack spacing={1.5}>
            {clients.map((c) => (
              <Box
                key={c.client_id}
                sx={{
                  border: '1px solid var(--rule)',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.9375rem' }}>
                    {c.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '0.8125rem',
                        color: 'var(--ink-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.client_id}
                    </Typography>
                    <Tooltip title={copiedId === c.client_id ? 'Copied' : 'Copy client ID'}>
                      <IconButton
                        size="small"
                        onClick={() => copyId(c.client_id)}
                        sx={{ borderRadius: 0, p: 0.25 }}
                        aria-label="Copy client ID"
                      >
                        {copiedId === c.client_id
                          ? <CheckOutlinedIcon sx={{ fontSize: 14 }} />
                          : <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Tooltip title="Rotate secret">
                  <IconButton
                    size="small"
                    onClick={() => onRotate(c.client_id)}
                    disabled={rotate.isPending}
                    sx={{ borderRadius: 0, border: '1px solid var(--rule)' }}
                    aria-label="Rotate secret"
                  >
                    <CachedOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete client">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget({ id: c.client_id, name: c.name })}
                    disabled={remove.isPending}
                    sx={{ borderRadius: 0, border: '1px solid var(--rule)' }}
                    aria-label="Delete client"
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Fraunces Variable', fontSize: '1.25rem' }}>
          Create new OAuth client
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Client name"
            placeholder="e.g. production-script"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') onCreate(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={onCreate}
            variant="contained"
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Fraunces Variable', fontSize: '1.25rem' }}>
          Delete client?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            All access tokens issued to this client will stop working immediately.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={onDelete}
            color="error"
            variant="contained"
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show new client_id + secret once */}
      <NewClientDialog result={createResult} onClose={() => setCreateResult(null)} />

      {/* Show rotated secret once */}
      <RotateSecretDialog
        result={rotateResult}
        clientId={rotateClientId}
        onClose={() => { setRotateResult(null); setRotateClientId(null); }}
      />
    </Card>
  );
}